import React, { useState, useMemo, useEffect } from "react";
import {
   collection,
   addDoc,
   updateDoc,
   deleteDoc,
   doc,
   serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import {
   FiPlus, FiTrash2, FiEdit2, FiCheck, FiX,
   FiGrid, FiLayers, FiMinimize, FiSettings, FiZap, FiBox,
   FiUsers
} from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { Switch, useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { showToast } from "../../../utils/toastUtils";

// Types
export interface Seat {
   id: string;
   name: string;
   row: number;
   col: number;
   status: 'available' | 'maintenance' | 'booked' | 'reserved';
}

export interface Floor {
   id: string;
   name: string;
   floorNumber: string;
   gridRows: number;
   gridCols: number;
   isActive: boolean;
   isVisible: boolean;
   seats: Seat[];
   createdAt: any;
}

interface ManagerFloorViewProps {
   floors: Floor[];
   currentOrgId: string;
   loading: boolean;
}

const ManagerFloorView: React.FC<ManagerFloorViewProps> = ({ floors, currentOrgId, loading }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const theme = getThemeClasses(isDark);
   const [isEditing, setIsEditing] = useState(false);
   const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
   const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'maintenance'>('all');

   // Form State for Create/Edit
   const [formData, setFormData] = useState({
      name: "",
      floorNumber: "1",
      gridRows: 4,
      gridCols: 8,
      isActive: true,
   });

   const [gridSeats, setGridSeats] = useState<Seat[]>([]);
   const [showFilterDropdown, setShowFilterDropdown] = useState(false);
   const [deleteModal, setDeleteModal] = useState<{ show: boolean; floorId: string; floorName: string }>({
      show: false,
      floorId: '',
      floorName: ''
   });

   // Filter floors based on status - MUST be before any conditional returns
   const filteredFloors = useMemo(() => {
      if (filterStatus === 'all') return floors;
      if (filterStatus === 'active') return floors.filter(f => f.isActive);
      return floors.filter(f => !f.isActive);
   }, [floors, filterStatus]);

   const handleFilterSelect = (status: 'all' | 'active' | 'maintenance') => {
      setFilterStatus(status);
      setShowFilterDropdown(false);
   };

   const handleDeleteFloor = async (floorId: string) => {
      try {
         await deleteDoc(doc(db, `organization/${currentOrgId}/floors`, floorId));
         setDeleteModal({ show: false, floorId: '', floorName: '' });
         showToast({ title: "Floor deleted successfully", status: "success", duration: 3000 });
      } catch (error) {
         console.error("Error deleting floor:", error);
         showToast({ title: "Failed to delete floor", status: "error", duration: 3000 });
      }
   };

   const openDeleteModal = (floorId: string, floorName: string) => {
      setDeleteModal({ show: true, floorId, floorName });
   };

   // Close dropdown when clicking outside
   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         const target = event.target as HTMLElement;
         if (showFilterDropdown && !target.closest('.filter-dropdown-container')) {
            setShowFilterDropdown(false);
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, [showFilterDropdown]);

   const handleCreateNew = () => {
      setSelectedFloor(null);
      setFormData({
         name: "",
         floorNumber: (floors.length + 1).toString(),
         gridRows: 4,
         gridCols: 8,
         isActive: true,
      });
      setGridSeats([]);
      setIsEditing(true);
   };

   const handleEditFloor = (floor: Floor) => {
      setSelectedFloor(floor);
      setFormData({
         name: floor.name,
         floorNumber: floor.floorNumber || "1",
         gridRows: floor.gridRows || 4,
         gridCols: floor.gridCols || 8,
         isActive: floor.isActive ?? true,
      });
      setGridSeats(floor.seats || []);
      setIsEditing(true);
   };

   const toggleSeat = (row: number, col: number) => {
      const existing = gridSeats.find(s => s.row === row && s.col === col);
      if (existing) {
         // Toggle status or remove? The image suggests we add seats.
         // Let's cycle through: Available -> Maintenance -> Removed
         if (existing.status === 'available') {
            setGridSeats(gridSeats.map(s => (s.row === row && s.col === col) ? { ...s, status: 'maintenance' } : s));
         } else if (existing.status === 'maintenance') {
            setGridSeats(gridSeats.filter(s => !(s.row === row && s.col === col)));
         }
      } else {
         const charCode = 65 + row; // A, B, C...
         const seatName = `${String.fromCharCode(charCode)}${col + 1}`;
         const newSeat: Seat = {
            id: `S-${Date.now()}-${row}-${col}`,
            name: seatName,
            row,
            col,
            status: 'available'
         };
         setGridSeats([...gridSeats, newSeat]);
      }
   };

   const handleAutoArrange = () => {
      const newSeats: Seat[] = [];
      for (let r = 0; r < formData.gridRows; r++) {
         for (let c = 0; c < formData.gridCols; c++) {
            const charCode = 65 + r;
            newSeats.push({
               id: `S-${Date.now()}-${r}-${c}`,
               name: `${String.fromCharCode(charCode)}${c + 1}`,
               row: r,
               col: c,
               status: 'available'
            });
         }
      }
      setGridSeats(newSeats);
   };

   const handleAddSingleSeat = () => {
      // Find the next available position in the grid
      const totalCells = formData.gridRows * formData.gridCols;
      const currentSeats = gridSeats.filter(s => s.row >= 0); // Only grid seats

      if (currentSeats.length >= totalCells) {
         // Grid is full, expand columns
         const newCols = formData.gridCols + 1;
         setFormData({ ...formData, gridCols: newCols });

         // Add seat to first row, new column
         const newSeat: Seat = {
            id: `S-${Date.now()}-0-${formData.gridCols}`,
            name: `A${newCols}`,
            row: 0,
            col: formData.gridCols,
            status: 'available'
         };
         setGridSeats([...gridSeats, newSeat]);
         showToast({ title: `Added ${newSeat.name} (expanded grid to ${formData.gridRows}×${newCols})`, status: "success" });
      } else {
         // Find next empty cell in grid
         for (let r = 0; r < formData.gridRows; r++) {
            for (let c = 0; c < formData.gridCols; c++) {
               const exists = gridSeats.some(s => s.row === r && s.col === c);
               if (!exists) {
                  const charCode = 65 + r;
                  const newSeat: Seat = {
                     id: `S-${Date.now()}-${r}-${c}`,
                     name: `${String.fromCharCode(charCode)}${c + 1}`,
                     row: r,
                     col: c,
                     status: 'available'
                  };
                  setGridSeats([...gridSeats, newSeat]);
                  showToast({ title: `Added ${newSeat.name}`, status: "success" });
                  return;
               }
            }
         }
      }
   };

   const handleRemoveSeat = () => {
      if (gridSeats.length === 0) {
         showToast({ title: "No seats to remove", status: "warning" });
         return;
      }
      // Remove the last seat
      const lastSeat = gridSeats[gridSeats.length - 1];
      setGridSeats(gridSeats.slice(0, -1));
      showToast({ title: `Removed ${lastSeat.name}`, status: "info" });
   };

   const handleClearGrid = () => {
      setGridSeats([]);
   };

   const handleSave = async () => {
      if (!formData.name) {
         showToast({ title: "Name is required", status: "error" });
         return;
      }

      const floorData = {
         ...formData,
         seats: gridSeats,
         isVisible: true,
         updatedAt: serverTimestamp()
      };

      try {
         if (selectedFloor) {
            await updateDoc(doc(db, `organization/${currentOrgId}/floors`, selectedFloor.id), floorData);
            showToast({ title: "Floor updated", status: "success" });
         } else {
            await addDoc(collection(db, `organization/${currentOrgId}/floors`), {
               ...floorData,
               createdAt: serverTimestamp()
            });
            showToast({ title: "Floor created", status: "success" });
         }
         setIsEditing(false);
      } catch (error) {
         console.error("Save error", error);
         showToast({ title: "Error saving floor", status: "error" });
      }
   };

   if (isEditing) {
      return (
         <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className={`${theme.card} p-4 rounded-xl border ${theme.border} shadow-sm`}>
               <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20 ring-1 ring-[#B59143]/30' : 'bg-gradient-to-br from-[#FBD282] to-[#B59143]'}`}>
                     <IconWrapper icon={FiLayers} size={20} className={isDark ? 'text-[#FBD282]' : 'text-white'} />
                  </div>
                  <h2 className={`text-xl font-bold ${theme.text}`}>{selectedFloor ? "Edit Floor" : "Create New Floor"}</h2>
               </div>
               <button onClick={() => setIsEditing(false)} className={`p-2 ${theme.textMuted} hover:${theme.text} absolute top-4 right-4`}>
                  <IconWrapper icon={FiX} size={24} />
               </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
               {/* Form Side */}
               <div className={`lg:col-span-4 ${theme.card} p-6 rounded-2xl border ${theme.border} shadow-sm flex flex-col gap-6`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                        <label className={`text-[10px] font-black uppercase tracking-widest ${theme.textMuted} mb-2 block`}>Floor Name</label>
                        <input
                           value={formData.name}
                           onChange={e => setFormData({ ...formData, name: e.target.value })}
                           className={`w-full ${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#B59143]/20 outline-none transition-all`}
                           placeholder="e.g. Tech Hub - L1"
                        />
                     </div>
                     <div>
                        <label className={`text-[10px] font-black uppercase tracking-widest ${theme.textMuted} mb-2 block`}>Floor Number</label>
                        <input
                           value={formData.floorNumber}
                           onChange={e => setFormData({ ...formData, floorNumber: e.target.value })}
                           className={`w-full ${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#B59143]/20 outline-none transition-all`}
                           placeholder="1"
                        />
                     </div>
                     <div>
                        <label className={`text-[10px] font-black uppercase tracking-widest ${theme.textMuted} mb-2 block`}>Grid Rows</label>
                        <input
                           type="number"
                           value={formData.gridRows}
                           onChange={e => setFormData({ ...formData, gridRows: parseInt(e.target.value) || 0 })}
                           className={`w-full ${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#B59143]/20 outline-none transition-all`}
                        />
                     </div>
                     <div>
                        <label className={`text-[10px] font-black uppercase tracking-widest ${theme.textMuted} mb-2 block`}>Grid Columns</label>
                        <input
                           type="number"
                           value={formData.gridCols}
                           onChange={e => setFormData({ ...formData, gridCols: parseInt(e.target.value) || 0 })}
                           className={`w-full ${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#B59143]/20 outline-none transition-all`}
                        />
                     </div>
                     <div className="flex flex-col">
                        <label className={`text-[10px] font-black uppercase tracking-widest ${theme.textMuted} mb-2 block`}>Status</label>
                        <div className="flex items-center gap-3 h-full">
                           <Switch
                              colorScheme="green"
                              isChecked={formData.isActive}
                              onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                           />
                           <span className={`text-xs font-bold ${theme.textMuted}`}>Active for Booking</span>
                        </div>
                     </div>
                  </div>

                  {/* Legend & Tool Actions */}
                  <div className={`flex flex-wrap items-center justify-between gap-4 pt-4 border-t ${theme.border}`}>
                     <div className="flex flex-col gap-2">
                        <p className={`text-[10px] font-black ${theme.textMuted} uppercase tracking-widest mb-1`}>Click Grid Cells to Toggle Status</p>
                        <div className="flex gap-4">
                           <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-100 border border-green-500"></div><span className={`text-[10px] font-bold ${theme.textMuted}`}>Available</span></div>
                           <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-100 border border-blue-500"></div><span className={`text-[10px] font-bold ${theme.textMuted}`}>Booked</span></div>
                           <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-orange-100 border border-orange-500"></div><span className={`text-[10px] font-bold ${theme.textMuted}`}>Reserved</span></div>
                           <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-100 border border-red-500 text-red-500 flex items-center justify-center text-[8px] font-black">×</div><span className={`text-[10px] font-bold ${theme.textMuted}`}>Maintenance</span></div>
                        </div>
                     </div>
                     <div className="flex gap-2">
                        <button onClick={handleClearGrid} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${theme.border} text-[10px] font-black uppercase tracking-tighter ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}`}><IconWrapper icon={FiTrash2} size={12} /> Clear All</button>
                        <button onClick={handleAutoArrange} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${theme.border} text-[10px] font-black uppercase tracking-tighter ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}`}><IconWrapper icon={FiZap} size={12} /> Auto Fill</button>
                        <button onClick={handleRemoveSeat} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-[10px] font-black uppercase tracking-tighter hover:bg-red-50"><IconWrapper icon={FiTrash2} size={12} /> Remove Seat</button>
                        <button onClick={handleAddSingleSeat} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#B59143] text-white text-[10px] font-black uppercase tracking-tighter hover:bg-[#A37F33] shadow-md"><IconWrapper icon={FiPlus} size={12} /> Add Seat</button>
                     </div>
                  </div>

                  {/* Grid Canvas */}
                  <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50/50'} rounded-2xl border ${theme.border} p-8 overflow-x-auto min-h-[400px] flex justify-center`}>
                     <div
                        className="grid gap-4 w-fit"
                        style={{
                           gridTemplateColumns: `repeat(${formData.gridCols}, minmax(60px, 1fr))`,
                           gridTemplateRows: `repeat(${formData.gridRows}, 60px)`
                        }}
                     >
                        {Array.from({ length: formData.gridRows }).map((_, r) => (
                           Array.from({ length: formData.gridCols }).map((_, c) => {
                              const seat = gridSeats.find(s => s.row === r && s.col === c);
                              const charCode = 65 + r;
                              return (
                                 <div
                                    key={`${r}-${c}`}
                                    onClick={() => toggleSeat(r, c)}
                                    className={`
                                   relative cursor-pointer rounded-xl border flex flex-col items-center justify-center transition-all duration-200
                                   ${seat
                                          ? seat.status === 'maintenance'
                                             ? 'bg-red-50 border-red-200 text-red-500'
                                             : 'bg-white border-green-500 text-green-600 shadow-sm'
                                          : 'bg-white/30 border-dashed border-gray-300 hover:border-[#B59143] hover:bg-white'
                                       }
                                   h-[60px] w-[60px]
                                `}
                                 >
                                    {seat ? (
                                       <>
                                          <IconWrapper icon={FiBox} size={14} className="mb-0.5" />
                                          <span className="text-[10px] font-black">{seat.name}</span>
                                          {seat.status === 'maintenance' && (
                                             <div className="absolute top-0 right-0 p-0.5 text-[8px] font-black">×</div>
                                          )}
                                       </>
                                    ) : (
                                       <span className="text-[8px] text-gray-300 font-bold">{String.fromCharCode(charCode)}{c + 1}</span>
                                    )}
                                 </div>
                              );
                           })
                        ))}
                     </div>
                     {/* Row/Col Labels can be added here if needed */}
                  </div>

                  <div className={`flex justify-end gap-3 pt-6 border-t ${theme.border}`}>
                     <button
                        onClick={() => setIsEditing(false)}
                        className={`px-6 py-2.5 rounded-xl border ${theme.border} text-xs font-black uppercase tracking-widest ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}`}
                     >
                        Cancel
                     </button>
                     <button
                        onClick={handleSave}
                        className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white text-xs font-black uppercase tracking-widest shadow-lg hover:shadow-xl active:scale-95 transition-all"
                     >
                        Save Configuration
                     </button>
                  </div>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
         {/* Sidebar: Floor List */}
         <div className="lg:col-span-1 flex flex-col gap-4">
            {/* Add New Floor Button - Moved to Top */}
            <button
               onClick={handleCreateNew}
               className="group relative w-full py-2.5 bg-gradient-to-r from-[#FBD282] to-[#B59143] hover:from-[#E8C16C] hover:to-[#A37F33] text-white rounded-lg font-semibold text-sm shadow-md shadow-[#B59143]/20 hover:shadow-lg hover:shadow-[#B59143]/25 flex items-center justify-center gap-2 transition-all duration-300 active:scale-[0.98] overflow-hidden"
            >
               <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>
               <IconWrapper icon={FiPlus} size={16} className="relative z-10" />
               <span className="relative z-10">Add New Floor</span>
            </button>

            <div className={`flex justify-between items-center px-2 ${isDark ? 'border-b border-[#2E2F33]' : 'border-b border-gray-200'} pb-3`}>
               <div className="flex items-center gap-2">
                  <h3 className={`text-base font-bold ${theme.text}`}>Floors</h3>
                  {/* Count Badge */}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-gray-100 text-[#B59143]'}`}>
                     {filteredFloors.length}
                  </span>
               </div>

               {/* Filter Dropdown */}
               <div className="relative filter-dropdown-container">
                  <button
                     onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                     className={`p-1.5 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-100'} rounded-lg transition-colors ${filterStatus === 'all' ? theme.textMuted : 'text-[#B59143]'
                        }`}
                  >
                     <IconWrapper icon={FiGrid} size={16} />
                  </button>

                  {/* Dropdown Menu */}
                  {showFilterDropdown && (
                     <div className={`absolute right-0 top-full mt-2 ${theme.card} border ${theme.border} rounded-lg shadow-lg overflow-hidden z-20 min-w-[160px]`}>
                        <button
                           onClick={() => handleFilterSelect('all')}
                           className={`w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors flex items-center justify-between ${filterStatus === 'all'
                                 ? isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-gray-100 text-[#B59143]'
                                 : isDark ? 'hover:bg-[#2A3441] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                              }`}
                        >
                           <span>All Floors</span>
                           <span className={`text-xs ${filterStatus === 'all' ? 'text-[#B59143]' : theme.textMuted}`}>
                              {floors.length}
                           </span>
                        </button>
                        <button
                           onClick={() => handleFilterSelect('active')}
                           className={`w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors flex items-center justify-between ${filterStatus === 'active'
                                 ? isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-gray-100 text-[#B59143]'
                                 : isDark ? 'hover:bg-[#2A3441] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                              }`}
                        >
                           <span>Active Only</span>
                           <span className={`text-xs ${filterStatus === 'active' ? 'text-[#B59143]' : theme.textMuted}`}>
                              {floors.filter(f => f.isActive).length}
                           </span>
                        </button>
                        <button
                           onClick={() => handleFilterSelect('maintenance')}
                           className={`w-full px-4 py-2.5 text-left text-sm font-semibold transition-colors flex items-center justify-between ${filterStatus === 'maintenance'
                                 ? isDark ? 'bg-[#2A3441] text-[#FBD282]' : 'bg-gray-100 text-[#B59143]'
                                 : isDark ? 'hover:bg-[#2A3441] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                              }`}
                        >
                           <span>Maintenance</span>
                           <span className={`text-xs ${filterStatus === 'maintenance' ? 'text-[#B59143]' : theme.textMuted}`}>
                              {floors.filter(f => !f.isActive).length}
                           </span>
                        </button>
                     </div>
                  )}
               </div>
            </div>

            {/* Scrollable Floor List - Hidden Scrollbar */}
            <div className="flex flex-col gap-3 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-hide">
               {loading ? (
                  <div className={`p-4 text-center ${theme.textMuted} text-[10px] uppercase tracking-widest`}>Loading Infrastructure...</div>
               ) : filteredFloors.length === 0 ? (
                  <div className={`p-10 border-2 border-dashed ${theme.border} rounded-2xl text-center ${theme.textMuted}`}>
                     <IconWrapper icon={FiMinimize} size={24} className="mx-auto mb-2 opacity-50" />
                     <p className="text-[10px] font-bold uppercase tracking-widest">
                        {filterStatus === 'all' ? 'No Floors Defined' : `No ${filterStatus === 'active' ? 'Active' : 'Maintenance'} Floors`}
                     </p>
                  </div>
               ) : (
                  filteredFloors.map(floor => (
                     <div
                        key={floor.id}
                        className={`group relative ${theme.card} border ${theme.border} p-3.5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer`}
                        onClick={() => handleEditFloor(floor)}
                     >
                        <div className="flex justify-between items-start mb-2.5">
                           <div className="flex items-center gap-2.5">
                              <div className={`p-1.5 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#FBD282]/15 to-[#B59143]/15 ring-1 ring-[#B59143]/25' : 'bg-gradient-to-br from-[#FBD282]/30 to-[#B59143]/30'}`}>
                                 <IconWrapper icon={FiLayers} size={14} className="text-[#B59143]" />
                              </div>
                              <div>
                                 <h4 className={`text-sm font-bold ${isDark ? 'text-white group-hover:text-[#FBD282]' : 'text-gray-900 group-hover:text-[#B59143]'} transition-colors`}>{floor.name}</h4>
                                 <p className={`text-[9px] ${theme.textMuted} font-semibold uppercase tracking-wide`}>Floor {floor.floorNumber}</p>
                              </div>
                           </div>
                           <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wide ${floor.isActive ? 'bg-green-500/20 text-green-600 ring-1 ring-green-500/30' : 'bg-orange-500/20 text-orange-600 ring-1 ring-orange-500/30'}`}>
                              {floor.isActive ? 'Active' : 'Maintenance'}
                           </span>
                        </div>

                        {/* Seat Count with Icon */}
                        <div className={`flex items-center gap-2 mb-2.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                           <IconWrapper icon={FiBox} size={12} className={theme.textMuted} />
                           <span className={`text-[11px] font-bold ${theme.text}`}>{floor.seats?.length || 0}</span>
                           <span className={`text-[9px] font-semibold ${theme.textMuted}`}>Seats Available</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                           <button
                              className={`flex-1 p-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} rounded-lg ${theme.textMuted} hover:${theme.text} flex items-center justify-center transition-colors`}
                              onClick={(e) => { e.stopPropagation(); handleEditFloor(floor); }}
                           >
                              <IconWrapper icon={FiEdit2} size={13} />
                           </button>
                           <button
                              className="flex-1 p-1.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-500 hover:text-red-600 flex items-center justify-center transition-colors"
                              onClick={(e) => { e.stopPropagation(); openDeleteModal(floor.id, floor.name); }}
                           >
                              <IconWrapper icon={FiTrash2} size={13} />
                           </button>
                        </div>
                     </div>
                  ))
               )}
            </div>
         </div>

         {/* Main Content Area: Visual Illustration */}
         <div className="lg:col-span-3 flex flex-col gap-6">
            <div className={`${theme.card} rounded-2xl border ${theme.border} p-8 min-h-[600px] flex items-center justify-center relative overflow-hidden`}>
               {/* Decorative Background Elements */}
               <div className="absolute inset-0 opacity-[0.03]">
                  <div className="absolute top-0 left-0 w-full h-full" style={{
                     backgroundImage: `repeating-linear-gradient(0deg, ${isDark ? '#B59143' : '#B59143'} 0px, transparent 1px, transparent 40px),
                                   repeating-linear-gradient(90deg, ${isDark ? '#B59143' : '#B59143'} 0px, transparent 1px, transparent 40px)`
                  }}></div>
               </div>

               {/* Main Visual Content */}
               <div className="relative z-10 w-full max-w-4xl">
                  {/* Instructional Text */}
                  <div className={`text-center mb-8 px-6 py-3 rounded-xl ${isDark ? 'bg-[#1A1C1F]' : 'bg-gradient-to-r from-gray-50 to-gray-100'} border ${theme.border}`}>
                     <p className={`text-sm ${theme.text} font-semibold`}>
                        👈 Click on a floor card to configure its layout
                     </p>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-10">
                     <h2 className={`text-3xl font-bold mb-2 bg-gradient-to-r from-[#FBD282] to-[#B59143] bg-clip-text text-transparent`}>
                        Floor & Seat Management
                     </h2>
                     <p className={`text-sm ${theme.textMuted}`}>Visual workspace allocation system</p>
                  </div>

                  {/* Visual Seat Grid Illustration */}
                  <div className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} rounded-2xl border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} p-8 relative`}>
                     {/* Room Label */}
                     <div className={`absolute -top-3 left-8 px-4 py-1.5 ${theme.card} border ${theme.border} rounded-lg`}>
                        <span className={`text-xs font-bold ${theme.text}`}>Office Floor Layout</span>
                     </div>

                     {/* Decorative Seat Grid */}
                     <div className="grid grid-cols-8 gap-3">
                        {/* Row 1 */}
                        {[
                           { name: 'A1', status: 'available' },
                           { name: 'A2', status: 'booked' },
                           { name: 'A3', status: 'available' },
                           { name: 'A4', status: 'booked' },
                           { name: 'A5', status: 'available' },
                           { name: 'A6', status: 'available' },
                           { name: 'A7', status: 'available' },
                           { name: 'A8', status: 'available' },
                        ].map((seat, idx) => (
                           <div
                              key={`r1-${idx}`}
                              className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center transition-all hover:scale-105 ${seat.status === 'available'
                                    ? isDark
                                       ? 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/40 text-green-400'
                                       : 'bg-gradient-to-br from-green-50 to-green-100 border-green-400 text-green-700'
                                    : isDark
                                       ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-blue-400'
                                       : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-400 text-blue-700'
                                 }`}
                           >
                              <IconWrapper icon={FiBox} size={16} className="mb-0.5" />
                              <span className="text-[9px] font-black">{seat.name}</span>
                           </div>
                        ))}

                        {/* Row 2 */}
                        {[
                           { name: 'B1', status: 'booked' },
                           { name: 'B2', status: 'available' },
                           { name: 'B3', status: 'reserved' },
                           { name: 'B4', status: 'booked' },
                           { name: 'B5', status: 'reserved' },
                           { name: 'B6', status: 'empty' },
                           { name: 'B7', status: 'available' },
                           { name: 'B8', status: 'available' },
                        ].map((seat, idx) => (
                           <div
                              key={`r2-${idx}`}
                              className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center transition-all hover:scale-105 ${seat.status === 'available'
                                    ? isDark
                                       ? 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/40 text-green-400'
                                       : 'bg-gradient-to-br from-green-50 to-green-100 border-green-400 text-green-700'
                                    : seat.status === 'booked'
                                       ? isDark
                                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-blue-400'
                                          : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-400 text-blue-700'
                                       : seat.status === 'reserved'
                                          ? isDark
                                             ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/40 text-orange-400'
                                             : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400 text-orange-700'
                                          : `border-dashed ${isDark ? 'border-[#2E2F33]' : 'border-gray-300'} opacity-30`
                                 }`}
                           >
                              {seat.status !== 'empty' && (
                                 <>
                                    <IconWrapper icon={FiBox} size={16} className="mb-0.5" />
                                    <span className="text-[9px] font-black">{seat.name}</span>
                                 </>
                              )}
                           </div>
                        ))}

                        {/* Row 3 */}
                        {[
                           { name: 'C1', status: 'available' },
                           { name: 'C2', status: 'booked' },
                           { name: 'C3', status: 'empty' },
                           { name: 'C4', status: 'reserved' },
                           { name: 'C5', status: 'available' },
                           { name: 'C6', status: 'available' },
                           { name: 'C7', status: 'available' },
                           { name: 'C8', status: 'booked' },
                        ].map((seat, idx) => (
                           <div
                              key={`r3-${idx}`}
                              className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center transition-all hover:scale-105 ${seat.status === 'available'
                                    ? isDark
                                       ? 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/40 text-green-400'
                                       : 'bg-gradient-to-br from-green-50 to-green-100 border-green-400 text-green-700'
                                    : seat.status === 'booked'
                                       ? isDark
                                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-blue-400'
                                          : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-400 text-blue-700'
                                       : seat.status === 'reserved'
                                          ? isDark
                                             ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/40 text-orange-400'
                                             : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400 text-orange-700'
                                          : `border-dashed ${isDark ? 'border-[#2E2F33]' : 'border-gray-300'} opacity-30`
                                 }`}
                           >
                              {seat.status !== 'empty' && (
                                 <>
                                    <IconWrapper icon={FiBox} size={16} className="mb-0.5" />
                                    <span className="text-[9px] font-black">{seat.name}</span>
                                 </>
                              )}
                           </div>
                        ))}

                        {/* Row 4 */}
                        {[
                           { name: 'D1', status: 'reserved' },
                           { name: 'D2', status: 'available' },
                           { name: 'D3', status: 'booked' },
                           { name: 'D4', status: 'booked' },
                           { name: 'D5', status: 'empty' },
                           { name: 'D6', status: 'available' },
                           { name: 'D7', status: 'available' },
                           { name: 'D8', status: 'empty' },
                        ].map((seat, idx) => (
                           <div
                              key={`r4-${idx}`}
                              className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center transition-all hover:scale-105 ${seat.status === 'available'
                                    ? isDark
                                       ? 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/40 text-green-400'
                                       : 'bg-gradient-to-br from-green-50 to-green-100 border-green-400 text-green-700'
                                    : seat.status === 'booked'
                                       ? isDark
                                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/40 text-blue-400'
                                          : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-400 text-blue-700'
                                       : seat.status === 'reserved'
                                          ? isDark
                                             ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/40 text-orange-400'
                                             : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400 text-orange-700'
                                          : `border-dashed ${isDark ? 'border-[#2E2F33]' : 'border-gray-300'} opacity-30`
                                 }`}
                           >
                              {seat.status !== 'empty' && (
                                 <>
                                    <IconWrapper icon={FiBox} size={16} className="mb-0.5" />
                                    <span className="text-[9px] font-black">{seat.name}</span>
                                 </>
                              )}
                           </div>
                        ))}
                     </div>

                     {/* Legend */}
                     <div className="flex items-center justify-center gap-6 mt-8">
                        <div className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded ${isDark ? 'bg-green-500/40' : 'bg-green-400'}`}></div>
                           <span className={`text-xs font-semibold ${theme.textMuted}`}>Available</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded ${isDark ? 'bg-blue-500/40' : 'bg-blue-400'}`}></div>
                           <span className={`text-xs font-semibold ${theme.textMuted}`}>Booked</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <div className={`w-3 h-3 rounded ${isDark ? 'bg-orange-500/40' : 'bg-orange-400'}`}></div>
                           <span className={`text-xs font-semibold ${theme.textMuted}`}>Reserved</span>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {/* Custom Delete Confirmation Modal */}
         {deleteModal.show && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
               <div className={`${theme.card} rounded-2xl border ${theme.border} shadow-2xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200`}>
                  {/* Modal Header */}
                  <div className={`px-6 py-4 border-b ${theme.border}`}>
                     <h3 className={`text-lg font-bold ${theme.text} flex items-center gap-2`}>
                        <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                           <IconWrapper icon={FiTrash2} size={20} className="text-red-500" />
                        </div>
                        Delete Floor
                     </h3>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-6">
                     <p className={`${theme.text} mb-2`}>
                        Are you sure you want to delete <strong className="text-[#B59143]">{deleteModal.floorName}</strong>?
                     </p>
                     <p className={`text-sm ${theme.textMuted}`}>
                        This action will permanently delete the floor and all its seats. This cannot be undone.
                     </p>
                  </div>

                  {/* Modal Footer */}
                  <div className={`px-6 py-4 border-t ${theme.border} flex gap-3 justify-end`}>
                     <button
                        onClick={() => setDeleteModal({ show: false, floorId: '', floorName: '' })}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${isDark
                              ? 'bg-[#2A3441] text-gray-300 hover:bg-[#3A4553]'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                           }`}
                     >
                        Cancel
                     </button>
                     <button
                        onClick={() => handleDeleteFloor(deleteModal.floorId)}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg"
                     >
                        Delete Floor
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

export default ManagerFloorView;
